<script lang="ts" setup>
import { ContentComp, DisplayTick, InputNullableDateTime, ListComp, SearchBox } from '@/components';
import ButtonComp from '@/components/Basic/ButtonComp.vue';
import type { GridColumnOptions } from '@/components/Grid/Classes';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import type { IGridRowCssRule } from '@/components/Grid/Classes/IGridRowCssRule';
import { useTaskLoader } from '@/composeables';
import { EventDao } from '@/dao';
import { EventinfoSearchlistItem, EventSearchRequest } from '@/objects/generated';
import type ListCompExpose from '@/objects/ListCompExpose';
import { useGuiStore } from '@/stores';
import { propertyof } from '@/utilities';
import { isToday } from '@/utilities/dateutils';
import { watchDebounced } from '@vueuse/core';

import { watch } from 'vue';
import { ref } from 'vue';

/**
 * This is the EventListView where we can search for events in a grid
 */

const listComp = ref<ListCompExpose<EventinfoSearchlistItem> | null>(null);

// The data for the grid
const data = ref<EventinfoSearchlistItem[]>([]);

// The searchtext from the custom toolbar
const searchText = ref<string>('');

// We want to change this from the default value of 100 to 1000 so we can move the focus to todays event. ( This assumes that there is not more than 1000 events in the period from today to toDate)
// NOTE! We can only focus on a row as long as it has been rendered.
const renderNumberOfRows = 1000;

// Helper property to determine if the initial state is loaded or not. We need to know this in order to know if the view has been visible or not since we dont want to load this data unelss the view is visible.
const initialLoad = ref<boolean>(false);

//Create a FromDate that is the last 6 months
const fromDate = ref<Date | null>(new Date(new Date().setMonth(new Date().getMonth() - 6)));

// Create a toDate that is 1 month ahead
const toDate = ref<Date | null>(new Date(new Date().setMonth(new Date().getMonth() + 1)));

// Do we need to fetch next data from the backend or not
const needToLoadFromBackend = ref<boolean>(true);

// The loader for the task
const { AddLoader, LoadState } = useTaskLoader();

/**
 * Perform the search
 */
const performSearch = () => {
    if (needToLoadFromBackend.value) {
        loadFromBackend();
    } else {
        listComp.value?.dataUpdated(); // Just trigger the search ( the rowGetter )
    }
};

/**
 * Load the data from the backend
 */
const loadFromBackend = async () => {
    // if we have already loaded the data, we do not need to load it again
    if (!needToLoadFromBackend.value) {
        return;
    }

    AddLoader(async () => {
        // load the data from the backend

        const request = EventSearchRequest.FromJsValues({
            From: fromDate.value,
            To: toDate.value,
        });

        data.value = await EventDao.SearchEventinfoSearchlistItems(request);
        listComp.value?.dataUpdated();

        if (!initialLoad.value) {
            setTimeout(() => {
                // We find the first row that is before today and scroll to it.
                // We need a bit of an algoritm here. We should know the number of rows that are visible so we can match it there, but for now we assume the number of visible rows are 25.
                // The we need to try and get the first of today and then scroll 25 past it.

                let isTodayCounter = 0;
                let startCounter = false;
                for (const row of data.value) {
                    if (isToday(row.Date) && !startCounter) {
                        startCounter = true;
                    }
                    if (startCounter) {
                        isTodayCounter++;
                    }

                    if (isTodayCounter > 25) {
                        listComp.value?.ensureRowVisible(row.ID.toString());
                        listComp.value?.resizeColumns();
                        break;
                    }
                }
            }, 200);
        }

        needToLoadFromBackend.value = false;
        initialLoad.value = true;
    }, 'eventinfo.load');
};

/**
 * Add custom columns to the grid
 * TODO: Might want to change the contact columns to a custom column
 */
const addCustomColumns = (columns: GridColumnOptions<EventinfoSearchlistItem>[]) => {
    // Add a custom column in position 4 for HasResults as a DisplayTick
    new GridColumnOptionsBuilder<EventinfoSearchlistItem>(propertyof<EventinfoSearchlistItem>('HasResult'), 'eventinfo.hasresult')
        .ReadOnly()
        .SortDisabled()
        .Size(100, 100)
        .CellRenderer(DisplayTick, { dontShowFalse: true })
        .Insert(columns, 4);

    return columns;
};

/**
 * The row class rules for the grid
 */
const rowClassRules: IGridRowCssRule<EventinfoSearchlistItem>[] = [
    {
        className: 'ag-row-today',
        func: (data) => isToday(data.Date),
    },
];

// Whenever we change the dates, we need to load the data from the backend.
// We only need to refresh data from the backend when we change the dates because we have all the data in the frontend for each period.
watch([fromDate, toDate], () => (needToLoadFromBackend.value = true));

// Listen for changes on the search text and debounce it
watchDebounced(searchText, () => performSearch(), { debounce: 500 });

const initialize = (isVisibile: boolean) => {
    if (!initialLoad.value && isVisibile) {
        loadFromBackend();
    }
};
</script>

<template>
    <ContentComp
        class="h-100"
        :loadState="LoadState"
        :showAsModal="true"
        @isVisible="initialize"
    >
        <ListComp
            ref="listComp"
            class="h-100"
            v-model="data"
            :gui="useGuiStore().EventinfoSearchlistItem"
            :readonly="true"
            :hide-toolbar="true"
            :select-all="false"
            :numberOfRenderedRows="renderNumberOfRows"
            :customColumns="addCustomColumns"
            :row-class-rules="rowClassRules"
            :searchText="searchText"
        >
            <template #toolbar>
                <div class="d-flex gap-2 w-100">
                    <label
                        >{{ $tm('general.from') }}
                        <InputNullableDateTime
                            v-model:modelValue="fromDate"
                            format="DATE"
                        />
                    </label>
                    <label
                        >{{ $tm('general.to') }}
                        <InputNullableDateTime
                            v-model:modelValue="toDate"
                            format="DATE"
                        />
                    </label>
                    <label class="w-100 flex-grow-1"
                        >&nbsp;
                        <SearchBox
                            v-if="searchText !== undefined"
                            :placeholder="$tm('general.search')"
                            v-model="searchText"
                            class="w-100 flex-grow-1"
                        />
                    </label>
                    <label>
                        &nbsp;
                        <ButtonComp
                            icon="fa-search"
                            button-type="Primary"
                            @click="performSearch"
                            >{{ $tm('general.search') }}</ButtonComp
                        >
                    </label>
                </div>
            </template>
        </ListComp>
    </ContentComp>
</template>
